import type { Article } from '../types';
import { DEFAULT_AUTHOR } from '../types';

import websiteWidgetBody from './website-feedback-widget.md?raw';
import docsBody from './documentation-feedback.md?raw';
import aiAgentBody from './ai-agent-chatbot-feedback.md?raw';
import hipaaBody from './hipaa-compliant-feedback.md?raw';
import saasBody from './saas-product-feedback.md?raw';

export const useCases: Article[] = [
  {
    collection: 'use-cases',
    slug: 'website-feedback-widget',
    title: 'Website Feedback Widget',
    metaTitle: 'Website Feedback Widget — Embed in One Line | Pollenate',
    description:
      'Add a website feedback widget to any site with a single script tag. Collect one-tap ratings and comments, then act on them in a real-time dashboard. Free to start.',
    eyebrow: 'Use case',
    heroSubtitle:
      'Collect one-tap ratings and comments from real visitors on any page — embedded with a single line of code.',
    body: websiteWidgetBody,
    author: DEFAULT_AUTHOR,
    datePublished: '2026-06-22',
    dateModified: '2026-06-22',
    schemaType: 'Article',
    faqs: [
      { question: 'What is a website feedback widget?', answer: 'A website feedback widget is a small embeddable prompt that lets visitors rate a page or leave a comment in one tap, without filling out a long form.' },
      { question: 'How do I add a feedback widget to my website?', answer: 'Paste a single Pollenate script tag into your HTML with your inbox key and API key. It works on static HTML, WordPress, Webflow, Shopify, and any framework — no build step.' },
      { question: 'Is there a free website feedback widget?', answer: 'Yes. Pollenate has a free-forever Starter plan that includes a brandable widget, 5,000 events per month, and a real-time dashboard.' },
    ],
    related: [
      { label: 'Add a feedback widget to your website', to: '/guides/add-feedback-widget-to-website' },
      { label: 'Best website feedback widgets', to: '/alternatives/best-website-feedback-widgets' },
      { label: 'Pricing', to: '/pricing' },
    ],
  },
  {
    collection: 'use-cases',
    slug: 'documentation-feedback',
    title: 'Documentation Feedback',
    metaTitle: 'Documentation Feedback — "Was This Helpful?" | Pollenate',
    description:
      'Add "Was this helpful?" feedback to your docs and find the pages that confuse readers. Works with Docusaurus, MkDocs, GitBook, Mintlify, and any docs site.',
    eyebrow: 'Use case',
    heroSubtitle:
      'Add a "Was this helpful?" prompt to every article and find the docs pages that confuse readers — then fix them with evidence.',
    body: docsBody,
    author: DEFAULT_AUTHOR,
    datePublished: '2026-06-22',
    dateModified: '2026-06-22',
    schemaType: 'Article',
    faqs: [
      { question: 'How do I add feedback to a documentation site?', answer: 'Add the Pollenate script tag to your docs template. It drops into Docusaurus, MkDocs, GitBook, Mintlify, Nextra, or a custom static site with no build plugin.' },
      { question: 'Can documentation feedback open a GitHub issue?', answer: 'Yes. A Pollenate automation can post negative documentation feedback to Slack or open a GitHub issue with the page URL attached, so fixes enter your normal workflow.' },
      { question: 'What widget works best for docs?', answer: 'A "Was this helpful?" thumbs widget is the most common for docs, with optional text for the specific gap. Stars and emoji also work well.' },
    ],
    related: [
      { label: 'Add a feedback widget to your website', to: '/guides/add-feedback-widget-to-website' },
      { label: 'Collect feedback in React & Next.js', to: '/guides/collect-feedback-react-nextjs' },
      { label: 'Documentation', to: '/docs' },
    ],
  },
  {
    collection: 'use-cases',
    slug: 'ai-agent-chatbot-feedback',
    title: 'AI Agent & Chatbot Feedback',
    metaTitle: 'AI Agent & Chatbot Feedback Collection | Pollenate',
    description:
      'Capture user ratings on every AI agent or chatbot conversation through a collect API, MCP server, and skill file. Find where your LLM app breaks down and improve it.',
    eyebrow: 'Use case',
    heroSubtitle:
      'Measure whether your AI agent actually helped. Capture a rating and the reason after every conversation through an API built for software.',
    body: aiAgentBody,
    author: DEFAULT_AUTHOR,
    datePublished: '2026-06-22',
    dateModified: '2026-06-22',
    schemaType: 'Article',
    faqs: [
      { question: 'How do I collect feedback from an AI agent or chatbot?', answer: 'Your agent submits a rating through the Pollenate collect API after each conversation. Pollenate also ships an MCP server, a skill file, and an OpenAPI spec so agent frameworks can use it natively.' },
      { question: 'Can I attach the model and prompt version to feedback?', answer: 'Yes. You can attach arbitrary metadata — model, prompt version, tools used, session ID — to every submission, then segment and search by it.' },
      { question: 'How does Pollenate help improve an LLM app?', answer: 'AI semantic search clusters the conversations users rated poorly, so you can find which intents, prompts, or model versions underperform and fix them with evidence.' },
    ],
    related: [
      { label: 'SaaS & product feedback', to: '/use-cases/saas-product-feedback' },
      { label: 'AI agents & MCP docs', to: '/docs' },
      { label: 'Pricing', to: '/pricing' },
    ],
  },
  {
    collection: 'use-cases',
    slug: 'hipaa-compliant-feedback',
    title: 'HIPAA-Compliant Feedback & Surveys',
    metaTitle: 'HIPAA-Compliant Feedback & Surveys (BAA) | Pollenate',
    description:
      'Collect patient and member feedback with a privacy-first, HIPAA-ready architecture and a signed BAA on Enterprise plans. Encryption, access controls, and data residency.',
    eyebrow: 'Use case',
    heroSubtitle:
      'Collect patient and member feedback safely with a HIPAA-ready architecture, field-level encryption, and a signed BAA on Enterprise plans.',
    body: hipaaBody,
    author: DEFAULT_AUTHOR,
    datePublished: '2026-06-22',
    dateModified: '2026-06-22',
    schemaType: 'Article',
    faqs: [
      { question: 'Is Pollenate HIPAA-compliant?', answer: 'Pollenate is built with a privacy-first, HIPAA-ready architecture. Enterprise plans include a signed BAA, field-level encryption options, enforced 2FA, and data residency controls.' },
      { question: 'Does Pollenate sign a Business Associate Agreement (BAA)?', answer: 'Yes. A signed BAA is available on Enterprise plans, which is required before storing any Protected Health Information.' },
      { question: 'Can I collect anonymous patient feedback?', answer: 'Yes, and it is the recommended default. You can run anonymous NPS and CSAT for trends and reserve identified, BAA-covered feedback for cases that require follow-up.' },
    ],
    related: [
      { label: 'HIPAA-compliant feedback collection guide', to: '/guides/hipaa-compliant-feedback-collection' },
      { label: 'Pollenate vs Qualtrics', to: '/vs/qualtrics' },
      { label: 'Pricing', to: '/pricing' },
    ],
  },
  {
    collection: 'use-cases',
    slug: 'saas-product-feedback',
    title: 'SaaS & Product Feedback',
    metaTitle: 'SaaS Product Feedback Software | Pollenate',
    description:
      'Collect in-app product feedback, feature ratings, and NPS without interrupting users. Prioritize your roadmap with AI semantic search and catch churn signals early.',
    eyebrow: 'Use case',
    heroSubtitle:
      'Collect in-app ratings, feature feedback, and NPS — then prioritize your roadmap with evidence and catch churn signals early.',
    body: saasBody,
    author: DEFAULT_AUTHOR,
    datePublished: '2026-06-22',
    dateModified: '2026-06-22',
    schemaType: 'Article',
    faqs: [
      { question: 'How do I collect in-app product feedback?', answer: 'Embed Pollenate widgets where decisions happen in your app — after a feature is used, or as in-app NPS and CSAT. Every response is tagged with the screen, plan, and account it came from.' },
      { question: 'Can I segment product feedback by plan or cohort?', answer: 'Yes. Pass metadata such as plan tier, cohort, or experiment variant with each response and slice your feedback by those segments in the dashboard.' },
      { question: 'How does Pollenate help prioritize a roadmap?', answer: 'AI semantic search clusters feedback by theme so you can rank what users actually ask for, instead of reacting to the loudest anecdote.' },
    ],
    related: [
      { label: 'How to measure NPS', to: '/guides/how-to-measure-nps' },
      { label: 'Collect feedback in React & Next.js', to: '/guides/collect-feedback-react-nextjs' },
      { label: 'Best NPS tools', to: '/alternatives/best-nps-tools' },
    ],
  },
];
